import { NetworkStatus, useQuery } from "@apollo/client";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  LinearProgress,
  Stack,
  TextField,
} from "@mui/material";
import { useState } from "react";
import { gql } from "../../../__generated__";
import { Iso639, OrderDirection } from "../../../__generated__/graphql";
import DateRangePicker from "../../../components/DateRangePicker";
import ProductSelector from "../../../components/ProductSelector";
import Select from "../../../components/Select";
import TransportServiceAgreementModal from "../TransportServiceAgreementModal";
import Booking, { BookingData } from "./Booking";
import styles from "./index.module.scss";

export default function BookingsList() {
  const [search, setSearch] = useState("");
  const [productId, setProductId] = useState("");
  const [fromReserved, setFromReserved] = useState<null | string>(null);
  const [untilReserved, setUntilReserved] = useState<null | string>(null);
  const [fromCreated, setFromCreated] = useState<null | string>(null);
  const [untilCreated, setUntilCreated] = useState<null | string>(null);
  const [orderField, setOrderField] = useState("created");
  const [orderDirection, setOrderDirection] = useState<"DESC" | "ASC">("DESC");

  return (
    <>
      <Stack
        className={styles.filters}
        direction="row"
        justifyContent="space-between"
      >
        <Stack spacing={1}>
          <Stack className={styles.filterRow} direction="row">
            <TextField
              className={styles.searchField}
              size="small"
              label="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Box className={styles.productSelector}>
              <ProductSelector
                size="small"
                label="Tour"
                onChange={setProductId}
              />
            </Box>
          </Stack>
          <Stack className={styles.filterRow} direction="row">
            <DateRangePicker
              id="reserved"
              label="Reserved"
              from={fromReserved}
              setFrom={setFromReserved}
              until={untilReserved}
              setUntil={setUntilReserved}
            />
            <DateRangePicker
              id="created"
              label="Created"
              from={fromCreated}
              setFrom={setFromCreated}
              until={untilCreated}
              setUntil={setUntilCreated}
            />
          </Stack>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <Box className={styles.orderSelect}>
            <Select
              hideNone
              size="small"
              label="Order"
              value={orderField}
              options={[
                { id: "created", name: "Created" },
                { id: "reserved", name: "Reserved" },
              ]}
              onChange={(v) => setOrderField(v)}
            />
          </Box>
          <IconButton
            onClick={() =>
              setOrderDirection(orderDirection === "DESC" ? "ASC" : "DESC")
            }
          >
            {orderDirection === "ASC" ? (
              <ArrowUpwardIcon />
            ) : (
              <ArrowDownwardIcon />
            )}
          </IconButton>
        </Stack>
      </Stack>
      <Bookings
        search={search}
        productId={productId}
        reserved={{ from: fromReserved, until: untilReserved }}
        created={{ from: fromCreated, until: untilCreated }}
        order={{ field: orderField, direction: orderDirection }}
      />
    </>
  );
}

function Bookings(props: {
  search: string;
  productId: string;
  reserved: { from: string | null; until: string | null };
  created: { from: string | null; until: string | null };
  order: { field: string; direction: "DESC" | "ASC" };
}) {
  const BOOKINGS_LIMIT = 20;

  const [tsaModalBookingId, setTsaModalBookingId] = useState<string | null>(
    null,
  );

  const { data, loading, error, fetchMore, networkStatus } = useQuery(
    GET_BOOKINGS,
    {
      variables: {
        input: {
          search: props.search,
          productId: props.productId,
          reserved: props.reserved,
          created: props.created,
          order: {
            ...props.order,
            direction: props.order.direction as OrderDirection,
          },
          offset: 0,
          limit: BOOKINGS_LIMIT,
        },
        lang: Iso639.En,
      },
      notifyOnNetworkStatusChange: true,
    },
  );
  if (loading && networkStatus !== NetworkStatus.fetchMore)
    return <LinearProgress color="error" />;
  if (error) return <>ERROR</>;

  const totalBookings = data ? data.bookings.totalRecords : 0;
  const bookings = data ? data.bookings.records : [];

  if (totalBookings === 0) return <>No bookings found</>;

  return (
    <>
      <Grid container spacing={2}>
        {bookings.map((b, index) => (
          <Booking
            key={index}
            data={b as BookingData}
            onOpenTsa={() => setTsaModalBookingId(b.id)}
          />
        ))}
      </Grid>
      {totalBookings > bookings.length && <LoadMore />}
      <TransportServiceAgreementModal
        bookingId={tsaModalBookingId}
        onClose={() => setTsaModalBookingId(null)}
      />
    </>
  );

  function LoadMore() {
    const isFetchingMore = networkStatus === NetworkStatus.fetchMore;

    return (
      <Box className={styles.loadMoreContainer}>
        <Button
          className={styles.loadMoreButton}
          variant="contained"
          disabled={isFetchingMore}
          onClick={() =>
            fetchMore({
              variables: {
                input: {
                  search: props.search,
                  productId: props.productId,
                  reserved: props.reserved,
                  created: props.created,
                  order: props.order,
                  offset: bookings.length,
                  limit: BOOKINGS_LIMIT,
                },
              },
              updateQuery: (previousQueryResult, { fetchMoreResult }) => {
                return {
                  bookings: {
                    totalRecords: fetchMoreResult.bookings.totalRecords,
                    records: [
                      ...previousQueryResult.bookings.records,
                      ...fetchMoreResult.bookings.records,
                    ],
                  },
                };
              },
            })
          }
        >
          {isFetchingMore ? <CircularProgress size={30} /> : "Load More"}
        </Button>
      </Box>
    );
  }
}

const GET_BOOKINGS = gql(`
  query GetBookings($input: BookingsInput!, $lang: Iso639!) {
    bookings(input: $input) {
      totalRecords
      records {
        id
        created
        didAttend
        state
        email
        phoneNumber
        specialRequests
        sourceBookingId
        salesPartnerId
        canBeRefundedUntil
        cancelledBy
        cancellationReason

        guests {
          givenName
          familyName
        }

        group {
          date
          time

          product {
            translation(lang: $lang) {
              name
              meetingPlaceName
            }
            location {
              timezone
            }
            availabilityType
            hasAccomodationVehiclePickup
            hasDefaultVehiclePickup
            requiresTransportAgreement
            meetingPlaceGoogleMapsQuery {
              id
              placeId
              geoLocation {
                lat
                lng
              }
            }
            pickupLocationZone {
              center {
                lat
                lng
              }
              radius
            }
          }
        }
        details {
          quantity
          productPrice {
            minAge
            maxAge
            tags
          }
        }
        pickupLocation {
          name
          address
          query {
            placeId
            geoLocation {
              lat
              lng
            }
          }
        }
        payment {
          state
          amount
          currency
        }
      }
    }
  }
`);
